import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Copy, Gift } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { myProfileQuery, myReferralsQuery } from "@/lib/queries";
import { formatHeat } from "@/lib/behot";
import { privatePageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";
import { MyPageLink } from "@/components/my-page-link";

export const Route = createFileRoute("/app/referrals")({
  head: () =>
    privatePageMetadata(
      "/app/referrals",
      "Referrals | BeHot.Now",
      "Your referral links and Heat.",
    ),
  component: ReferralsPage,
});

function ReferralsPage() {
  const { t } = useT();
  const { user } = useAuth();
  const { data: profile } = useQuery(myProfileQuery(user?.id));
  const { data: referrals = [] } = useQuery(myReferralsQuery(user?.id));

  const link =
    typeof window !== "undefined" && profile?.referral_code
      ? `${window.location.origin}/?ref=${profile.referral_code}`
      : "";
  const earned = referrals.reduce((s, r) => s + Number(r.reward_cents), 0);

  return (
    <div className="max-w-2xl space-y-6">
      <MyPageLink />

      <div className="card-surface p-6">
        <p className="flex items-center gap-2 font-display text-lg font-bold">
          <Gift className="size-5 text-primary" /> {t("dashboard.referrals.yourLink")}
        </p>
        <p className="mt-1 text-sm text-muted-foreground">
          {t("dashboard.referrals.desc")}
        </p>
        <div className="mt-4 flex gap-2">
          <Input readOnly value={link} />
          <Button
            variant="hot"
            onClick={() => {
              void navigator.clipboard.writeText(link);
              toast.success(t("dashboard.referrals.copied"));
            }}
          >
            <Copy className="size-4" /> {t("dashboard.referrals.copy")}
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <div className="card-surface p-5">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {t("dashboard.referrals.invited")}
          </p>
          <p className="mt-1 font-display text-2xl font-bold">{referrals.length}</p>
        </div>
        <div className="card-surface p-5">
          <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {t("dashboard.referrals.creditsEarned")}
          </p>
          <p className="mt-1 font-display text-2xl font-bold">{t("dashboard.referrals.heatAmount", { amount: formatHeat(earned) })}</p>
        </div>
      </div>

      <div className="space-y-3">
        {referrals.length === 0 && (
          <div className="card-surface p-6 text-sm text-muted-foreground">
            {t("dashboard.referrals.empty")}
          </div>
        )}
        {referrals.map((r) => (
          <div key={r.id} className="card-surface flex items-center gap-3 p-4 text-sm">
            <span className="flex-1 text-muted-foreground">
              {t("dashboard.referrals.joined", { date: new Date(r.created_at).toLocaleDateString() })}
            </span>
            <Badge variant={r.status === "rewarded" ? "secondary" : "outline"}>{r.status}</Badge>
            <span className="font-semibold">{t("dashboard.referrals.heatAmount", { amount: formatHeat(r.reward_cents) })}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
